<script setup lang="ts">
const { t } = useI18n();

interface CampaignInfo {
  organization: string;
  title: string;
  goalAmount: number | null;
  currentAmount: number;
  donorCount: number;
}

defineProps<{
  organizationLogoUrl: string | null;
  campaignInfo: CampaignInfo;
  campaignImageUrl: string | null;
  formatAmount: (amount: number) => string;
  formatAmountShort: (amount: number) => string;
  finalAmountWithDiscount: number;
  progressPercentage: number;
  /** Barre de temps (date de fin posée) : instant de clôture + jour en clair. */
  closesAt?: string | null;
  endDateLabel?: string | null;
  daysRemaining?: number | null;
  startsAt?: string | null;
}>();

const emit = defineEmits<{
  /** La barre de temps est arrivée à zéro : la cagnotte vient de se clôturer. */
  (e: "ended"): void;
}>();
</script>

<template>
  <!-- Mobile Header -->
  <div class="lg:hidden space-y-4">
    <!-- Badge organisation et langue sur la même ligne : le nom se coupe
         (« … ») s'il est trop long (Karim 01/11/2026). -->
    <div class="flex items-center gap-2">
      <div
        class="inline-flex items-center min-w-0 px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700"
      >
        <img
          v-if="organizationLogoUrl"
          :src="organizationLogoUrl"
          :alt="campaignInfo.organization"
          class="w-4 h-4 mr-2 object-contain rounded-full shrink-0"
        />
        <UIcon
          v-else
          name="i-lucide-home"
          class="w-4 h-4 mr-2 shrink-0"
        />
        <span class="truncate">{{ campaignInfo.organization }}</span>
      </div>
      <div class="ml-auto shrink-0">
        <SelecteurLangue variante="clair" sens="bas" />
      </div>
    </div>

    <!-- Bannière image avec overlay.
         iOS Safari peint le `blur` de l'image dans une couche de compositing
         séparée que `overflow:hidden` ne clippe pas au même rayon que le
         `border-radius` → le fond paraissait très légèrement moins arrondi que
         l'overlay (double-clip qui diverge). Correctif définitif : une SEULE
         source de rayon via `clip-path: inset(0 round 40px)` — un clip
         géométrique qui s'impose à TOUTES les couches (image floutée + overlay)
         exactement au même rayon. Wrapper externe pour la box-shadow (que le
         `clip-path` couperait sinon). Karim 2026-07-10. -->
    <div class="rounded-[40px] shadow-lg">
      <div class="banner-clip relative">
        <img
          v-if="campaignImageUrl"
          :src="campaignImageUrl"
          :alt="campaignInfo.title"
          class="w-full h-48 object-cover blur-xs"
        />
        <div
          v-else
          class="w-full h-48 bg-linear-to-br from-gray-200 to-gray-300"
        />
        <!-- Overlay sombre uniforme -->
        <div class="absolute inset-0 bg-black/60" />
        <!-- Contenu sur l'image -->
        <div
          class="absolute inset-0 flex flex-col justify-center items-center text-white p-4"
        >
          <h1 class="text-lg font-semibold mb-1 text-center">
            {{ t('campagne.paiementMobile.participerTitre') }}
          </h1>
          <div class="text-4xl font-bold mb-2">
            {{ formatAmount(finalAmountWithDiscount) }}
          </div>
          <p class="text-sm text-white text-center line-clamp-2">
            {{ campaignInfo.title }}
          </p>
        </div>
      </div>
    </div>

    <!-- Progression compacte -->
    <div class="bg-gray-50 rounded-xl p-4 shadow-md">
      <div :class="['flex justify-between items-center text-sm', campaignInfo.goalAmount ? 'mb-2' : '']">
        <span class="font-bold text-gray-900">
          <template v-if="campaignInfo.goalAmount">
            {{ formatAmountShort(campaignInfo.currentAmount) }}
            <span class="text-gray-500 font-normal">
              / {{ formatAmountShort(campaignInfo.goalAmount) }}
            </span>
          </template>
          <template v-else>
            {{ t('campagne.paiementMobile.montantRecolte', { montant: formatAmountShort(campaignInfo.currentAmount) }) }}
          </template>
        </span>
        <span class="text-gray-500"
          >{{ t('campagne.paiementMobile.participants', campaignInfo.donorCount) }}</span
        >
      </div>
      <div
        v-if="campaignInfo.goalAmount"
        class="w-full h-2 bg-gray-200 rounded-full overflow-hidden"
      >
        <!-- La couleur des boutons de montant et du bouton de don
             (`--payment-primary`) — Karim 2026-09-30. -->
        <div
          class="h-full rounded-full transition-all duration-500"
          :style="{ width: `${progressPercentage}%`, background: 'var(--payment-primary)' }"
        />
      </div>
      <!-- Barre de temps : même règle que la page de la cagnotte. -->
      <CampaignCountdown
        v-if="closesAt && endDateLabel"
        :closes-at="closesAt"
        :starts-at="startsAt"
        :end-date-label="endDateLabel"
        :days-remaining="daysRemaining"
        compact
        class="mt-3"
        @ended="emit('ended')"
      />
    </div>

    <!-- Juste sous la progression (encart réduction d'impôt — Karim 2026-10-22). -->
    <slot name="apres-progression" />
  </div>
</template>

<style scoped>
/* Clip géométrique unique de la bannière hero : s'applique à TOUTES les
   couches (image floutée + overlay) au même rayon, contrairement à
   `overflow:hidden` que le compositing du blur ignore sur iOS Safari. Le
   `rounded-[40px]` utilitaire reste pour la box-shadow. */
.banner-clip {
  clip-path: inset(0 round 40px);
}
</style>
